import FieldInfo from '@/components/form/FieldInfo'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

export const PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS = 500

const normalizeDraftValue = (value) => (value == null ? '' : String(value))

export function usePreviewDraftField(
  field,
  commitDelayMs = PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS,
  { flushOnUnmount = true } = {}
) {
  const fieldValue = normalizeDraftValue(field.state.value)
  const [draftValue, setDraftValue] = useState(() => fieldValue)
  const [hasPendingChange, setHasPendingChange] = useState(false)
  const fieldRef = useRef(field)
  const draftValueRef = useRef(draftValue)
  const hasPendingChangeRef = useRef(false)
  const timerRef = useRef(null)

  useEffect(() => {
    fieldRef.current = field
  }, [field])

  const clearCommitTimer = useCallback(() => {
    if (!timerRef.current) {
      return
    }

    window.clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const commitDraftValue = useCallback(
    (options = {}) => {
      const updatePendingState = options.updatePendingState !== false

      clearCommitTimer()

      if (!hasPendingChangeRef.current) {
        return
      }

      hasPendingChangeRef.current = false

      const nextValue = draftValueRef.current
      const currentValue = normalizeDraftValue(fieldRef.current.state.value)

      if (nextValue !== currentValue) {
        fieldRef.current.handleChange(nextValue)
      }

      if (updatePendingState) {
        setHasPendingChange(false)
      }
    },
    [clearCommitTimer]
  )

  useEffect(
    () => () => {
      if (flushOnUnmount) {
        commitDraftValue({ updatePendingState: false })
      }

      clearCommitTimer()
    },
    [clearCommitTimer, commitDraftValue, flushOnUnmount]
  )

  const handleDraftChange = useCallback(
    (event) => {
      const nextValue = event.target.value

      draftValueRef.current = nextValue
      hasPendingChangeRef.current = true
      setHasPendingChange(true)
      setDraftValue(nextValue)

      clearCommitTimer()
      timerRef.current = window.setTimeout(commitDraftValue, commitDelayMs)
    },
    [clearCommitTimer, commitDelayMs, commitDraftValue]
  )

  const handleDraftBlur = useCallback(() => {
    commitDraftValue()
    fieldRef.current.handleBlur()
  }, [commitDraftValue])

  return {
    draftValue: hasPendingChange ? draftValue : fieldValue,
    handleDraftBlur,
    handleDraftChange,
  }
}

export function PreviewDraftTextInput({
  field,
  label = null,
  placeholder = '',
  readOnlyEmptyText = '',
  commitDelayMs = PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS,
  flushOnUnmount = true,
  className = null,
}) {
  const { draftValue, handleDraftBlur, handleDraftChange } = usePreviewDraftField(
    field,
    commitDelayMs,
    { flushOnUnmount }
  )
  const { isReadOnly } = useIsReadOnly()
  const randomId = useId()
  const inputId = `${randomId}-${field.name}`
  const readOnlyValue = normalizeDraftValue(field.state.value)

  return (
    <div className="flex w-full flex-col gap-y-2">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      {isReadOnly ? (
        <Typography variant="label" tone={!readOnlyValue ? 'muted' : 'default'}>
          {readOnlyValue || readOnlyEmptyText}
        </Typography>
      ) : (
        <Input
          id={inputId}
          placeholder={placeholder}
          value={draftValue}
          onChange={handleDraftChange}
          onBlur={handleDraftBlur}
          className={cn('w-full', className)}
        />
      )}
      <FieldInfo />
    </div>
  )
}

export function PreviewDraftTextareaInput({
  field,
  label = null,
  placeholder = '',
  readOnlyEmptyText = 'No description provided',
  commitDelayMs = PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS,
  flushOnUnmount = true,
  rows = 3,
  className = null,
}) {
  const { draftValue, handleDraftBlur, handleDraftChange } = usePreviewDraftField(
    field,
    commitDelayMs,
    { flushOnUnmount }
  )
  const { isReadOnly } = useIsReadOnly()
  const randomId = useId()
  const inputId = `${randomId}-${field.name}`
  const readOnlyValue = normalizeDraftValue(field.state.value)

  return (
    <div className="flex w-full flex-col gap-y-2">
      {label ? <Label htmlFor={inputId}>{label}</Label> : null}
      {isReadOnly ? (
        <Typography variant="supporting" tone={!readOnlyValue ? 'muted' : 'default'}>
          {readOnlyValue || readOnlyEmptyText}
        </Typography>
      ) : (
        <Textarea
          id={inputId}
          placeholder={placeholder}
          rows={rows}
          value={draftValue}
          onChange={handleDraftChange}
          onBlur={handleDraftBlur}
          className={cn('field-sizing-fixed', className)}
        />
      )}
      <FieldInfo />
    </div>
  )
}

export function PreviewDraftTextField({
  form,
  name,
  label,
  placeholder = '',
  readOnlyEmptyText = '',
  commitDelayMs = PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS,
  flushOnUnmount = true,
  className = null,
}) {
  return (
    <form.AppField name={name}>
      {(field) => (
        <PreviewDraftTextInput
          field={field}
          label={label}
          placeholder={placeholder}
          readOnlyEmptyText={readOnlyEmptyText}
          commitDelayMs={commitDelayMs}
          flushOnUnmount={flushOnUnmount}
          className={className}
        />
      )}
    </form.AppField>
  )
}

export function PreviewDraftTextareaField({
  form,
  name,
  label,
  placeholder = '',
  readOnlyEmptyText = 'No description provided',
  commitDelayMs = PREVIEW_DRAFT_FIELD_COMMIT_DELAY_MS,
  flushOnUnmount = true,
  rows = 3,
  className = null,
}) {
  return (
    <form.AppField name={name}>
      {(field) => (
        <PreviewDraftTextareaInput
          field={field}
          label={label}
          placeholder={placeholder}
          readOnlyEmptyText={readOnlyEmptyText}
          commitDelayMs={commitDelayMs}
          flushOnUnmount={flushOnUnmount}
          rows={rows}
          className={className}
        />
      )}
    </form.AppField>
  )
}
